// Google Identity Services: the authorisation-code flow in a popup. The code
// goes to the connectCalendar function, which holds the refresh token. Under
// the emulators with no client id, a mock code stands in for the popup.

import { GOOGLE_CALENDAR_SCOPES } from '@/config'
import { usingEmulators } from './firebase'

interface CodeResponse {
  code?: string
  error?: string
  error_description?: string
}

interface CodeClient {
  requestCode: () => void
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string
            scope: string
            ux_mode: 'popup'
            login_hint?: string
            prompt?: string
            access_type?: string
            callback: (r: CodeResponse) => void
            error_callback?: (e: { type: string; message?: string }) => void
          }) => CodeClient
        }
      }
    }
  }
}

let loading: Promise<void> | null = null

function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve()
  if (loading) return loading
  loading = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Could not load Google sign-in.'))
    document.head.appendChild(s)
  })
  return loading
}

export const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || ''

export const canConnectGoogle = !!clientId || usingEmulators

/**
 * Asks for an authorisation code in a popup, with the person's email as the
 * login hint. With `forceConsent`, Google shows the consent screen again and
 * issues a refresh token.
 */
export async function requestAuthCode(loginHint: string, forceConsent = false): Promise<{ code: string; redirectUri: string }> {
  if (!clientId) {
    if (usingEmulators) return { code: forceConsent ? 'mock-consent' : 'mock', redirectUri: 'postmessage' }
    throw new Error('Google Calendar is not set up yet: no OAuth client id.')
  }
  await loadGis()
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: GOOGLE_CALENDAR_SCOPES.join(' '),
      ux_mode: 'popup',
      login_hint: loginHint,
      access_type: 'offline',
      prompt: forceConsent ? 'consent' : '',
      callback: (r) => {
        if (r.code) resolve({ code: r.code, redirectUri: 'postmessage' })
        else reject(new Error(r.error_description || r.error || 'Google did not return a code.'))
      },
      error_callback: (e) => reject(new Error(e.type === 'popup_closed' ? 'cancelled' : e.message || e.type)),
    })
    client.requestCode()
  })
}
